import { api, type ApiResponse } from './fetchClient'
import {
  CarreteiroSyncResponseSchema,
  CatalogEnvelopeSchema,
  ChefeObraResponseSchema,
  PrecheckCarreteiroResponseSchema,
  type ChefeObraResponse,
  type PrecheckCarreteiroResponse,
  EquipeEncarregadoDtoSchema,
  FotoSignedUrlResponseSchema,
  FotoSyncResponseSchema,
  LocalCargaDtoSchema,
  LocalDescargaDtoSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  MaterialDtoSchema,
  MeResponseSchema,
  MveDtoSchema,
  NamedDtoSchema,
  NamedWithChapaDtoSchema,
  NamedWithProjetoDtoSchema,
  ObraDtoSchema,
  ProducaoBatchResponseSchema,
  ProjetoDtoSchema,
  ProjetoServicoDtoSchema,
  ServicoDtoSchema,
  TrechoDtoSchema,
  UnidadeDtoSchema,
  type CarreteiroSyncResponse,
  type FichaCarreteiroDto,
  type FotoMetadataDto,
  type FotoSignedUrlResponse,
  type FotoSyncResponse,
  type LoginRequest,
  type LoginResponse,
  type MeResponse,
  type ProducaoBatchResponse,
  type ProducaoRegistroDto,
} from '@/types/dtos'
import { z } from 'zod'
import {
  ContextoParteDiariaSchema,
  EquipeParteDtoSchema,
  MvesParteDiariaResponseSchema,
  ObraParteDtoSchema,
  PacoteOfflineParteDiariaSchema,
  ParteDiariaEppSyncResponseSchema,
  ParteDiariaMveSyncResponseSchema,
  ServicoParteDtoSchema,
  type ContextoParteDiariaDto,
  type EquipeParteDto,
  type LancamentoEppDto,
  type LancamentoMveDto,
  type MvesParteDiariaResponse,
  type ObraParteDto,
  type PacoteOfflineParteDiariaDto,
  type ParteDiariaEppSyncResponse,
  type ParteDiariaMveSyncResponse,
  type ServicoParteDto,
} from '@/types/parteDiariaDtos'

/**
 * Camada de endpoints tipados. Cada função:
 *  - Faz a chamada via `fetchClient`.
 *  - Valida o shape com zod (defensivo contra drift backend↔frontend).
 *  - Devolve `{ data, etag, status }` para o caller decidir o que fazer com 304/ETag.
 *
 * Substitui `MonitoApi.kt` (interface Retrofit) do Android.
 */

// ============================================================================
//  Auth
// ============================================================================
export const authEndpoints = {
  async login(body: LoginRequest): Promise<LoginResponse> {
    LoginRequestSchema.parse(body)
    const res = await api.post<unknown>('/auth/login', body)
    return LoginResponseSchema.parse(res.data)
  },

  async me(): Promise<MeResponse> {
    const res = await api.get<unknown>('/auth/me')
    return MeResponseSchema.parse(res.data)
  },
}

// ============================================================================
//  Catálogos — helper genérico
// ============================================================================
async function fetchCatalog<T extends z.ZodTypeAny>(
  path: string,
  itemSchema: T,
  ifNoneMatch?: string,
): Promise<ApiResponse<{ version: string; fetchedAt: string; items: z.infer<T>[] } | undefined>> {
  const envelopeSchema = CatalogEnvelopeSchema(itemSchema)
  const res = await api.get<unknown>(path, ifNoneMatch ? { ifNoneMatch } : undefined)
  if (res.status === 304) {
    return { data: undefined, etag: res.etag, status: 304 }
  }
  return { data: envelopeSchema.parse(res.data), etag: res.etag, status: res.status }
}

// ============================================================================
//  Catalog endpoints (18)
// ============================================================================
export const catalogEndpoints = {
  obras: (etag?: string) => fetchCatalog('/catalogs/obras', ObraDtoSchema, etag),
  projetos: (etag?: string) => fetchCatalog('/catalogs/projetos', ProjetoDtoSchema, etag),
  servicos: (etag?: string) => fetchCatalog('/catalogs/servicos', ServicoDtoSchema, etag),
  encarregados: (etag?: string) =>
    fetchCatalog('/catalogs/encarregados', NamedWithChapaDtoSchema, etag),
  motoristas: (etag?: string) =>
    fetchCatalog('/catalogs/motoristas', NamedWithChapaDtoSchema, etag),
  apontadores: (etag?: string) =>
    fetchCatalog('/catalogs/apontadores', NamedWithChapaDtoSchema, etag),
  equipes: (etag?: string) => fetchCatalog('/catalogs/equipes', NamedDtoSchema, etag),
  unidades: (etag?: string) => fetchCatalog('/catalogs/unidades', UnidadeDtoSchema, etag),
  materiais: (etag?: string) => fetchCatalog('/catalogs/materiais', MaterialDtoSchema, etag),
  /**
   * Versões legadas (sem projeto) — devolvem todo o catálogo. Mantidas por
   * compatibilidade caso outras telas precisem listar tudo.
   */
  materiaisRede: (etag?: string) =>
    fetchCatalog('/catalogs/materiais-rede', NamedWithProjetoDtoSchema, etag),
  diametros: (etag?: string) =>
    fetchCatalog('/catalogs/diametros', NamedWithProjetoDtoSchema, etag),
  tiposRede: (etag?: string) =>
    fetchCatalog('/catalogs/tipos-rede', NamedWithProjetoDtoSchema, etag),
  /**
   * Versões por projeto — usadas pelo módulo Produção. Backend filtra via
   * cdt_projeto_tem_{material_rede,diametro,tipo_rede}.
   */
  materiaisRedePorProjeto: (projetoId: number, etag?: string) =>
    fetchCatalog(
      `/catalogs/materiais-rede?projeto_id=${projetoId}`,
      NamedWithProjetoDtoSchema,
      etag,
    ),
  diametrosPorProjeto: (projetoId: number, etag?: string) =>
    fetchCatalog(
      `/catalogs/diametros?projeto_id=${projetoId}`,
      NamedWithProjetoDtoSchema,
      etag,
    ),
  tiposRedePorProjeto: (projetoId: number, etag?: string) =>
    fetchCatalog(
      `/catalogs/tipos-rede?projeto_id=${projetoId}`,
      NamedWithProjetoDtoSchema,
      etag,
    ),
  trechos: (etag?: string) => fetchCatalog('/catalogs/trechos', TrechoDtoSchema, etag),
  mves: (etag?: string) => fetchCatalog('/catalogs/mves', MveDtoSchema, etag),
  /**
   * Caminhões do chefe da obra na data (regra do SIGA web: histórico de
   * transferência + contrato forma 'C'). Sem ETag — depende da data. Backend
   * devolve 422 OBRA_SEM_CHEFE se a obra não tem chefe no histórico na data.
   */
  mvesPorObra: (obraId: number, dt: string) =>
    fetchCatalog(
      `/catalogs/mves?${new URLSearchParams({ obra_id: String(obraId), dt })}`,
      MveDtoSchema,
    ),
  /**
   * Serviços com item de contrato forma 'C' vigente do caminhão na data, com
   * garantia/preço/datas para a prévia offline. Sem ETag.
   */
  servicosCarreteiroPorMve: (mveId: number, dt: string) =>
    fetchCatalog(
      `/catalogs/servicos?${new URLSearchParams({ mve_id: String(mveId), dt })}`,
      ServicoDtoSchema,
    ),
  /** Chefe da ficha: chefe do projeto da obra na data. */
  async chefeObra(obraId: number, dt: string): Promise<ChefeObraResponse> {
    const res = await api.get<unknown>(
      `/catalogs/chefe-obra?${new URLSearchParams({ obra_id: String(obraId), dt })}`,
    )
    return ChefeObraResponseSchema.parse(res.data)
  },
  locaisCarga: (projetoId: number, etag?: string) =>
    fetchCatalog(`/catalogs/locais-carga?projeto_id=${projetoId}`, LocalCargaDtoSchema, etag),
  locaisDescargaRod: (etag?: string) =>
    fetchCatalog('/catalogs/locais-descarga?modal=rodoviario', LocalDescargaDtoSchema, etag),
  locaisDescargaUrb: (etag?: string) =>
    fetchCatalog('/catalogs/locais-descarga?modal=urbana', LocalDescargaDtoSchema, etag),
  projetoServico: (etag?: string) =>
    fetchCatalog('/catalogs/projeto-servico', ProjetoServicoDtoSchema, etag),
  equipeEncarregado: (etag?: string) =>
    fetchCatalog('/catalogs/equipe-encarregado', EquipeEncarregadoDtoSchema, etag),
}

// ============================================================================
//  Sync (escritas)
// ============================================================================
export const syncEndpoints = {
  async producao(registros: ProducaoRegistroDto[]): Promise<ProducaoBatchResponse> {
    const res = await api.post<unknown>('/sync/producao', { registros })
    return ProducaoBatchResponseSchema.parse(res.data)
  },

  async carreteiro(ficha: FichaCarreteiroDto): Promise<CarreteiroSyncResponse> {
    const res = await api.post<unknown>('/sync/carreteiro', ficha)
    // 2xx = ficha gravada no SIGA. Resposta fora do schema NÃO pode virar erro:
    // o outbox reenviaria e duplicaria a ficha.
    const parsed = CarreteiroSyncResponseSchema.safeParse(res.data)
    if (!parsed.success) {
      console.warn('[sync/carreteiro] resposta fora do schema (ficha aceita):', parsed.error.flatten())
      return res.data as CarreteiroSyncResponse
    }
    return parsed.data
  },

  /** Regras de cabeçalho do carreteiro (medição, transferência, contrato…) sem gravar. */
  async precheckCarreteiro(
    obraId: number,
    mveId: number,
    dt: string,
  ): Promise<PrecheckCarreteiroResponse> {
    const params = new URLSearchParams({ obra_id: String(obraId), mve_id: String(mveId), dt })
    const res = await api.get<unknown>(`/carreteiro/precheck?${params}`)
    return PrecheckCarreteiroResponseSchema.parse(res.data)
  },

  /**
   * Upload de uma foto. Multipart com `metadata` (JSON string) e `file` (Blob).
   * Backend é idempotente por `metadata.id`; resposta retorna `idempotent: true` se já existia.
   */
  async foto(metadata: FotoMetadataDto, file: Blob): Promise<FotoSyncResponse> {
    const form = new FormData()
    form.append('metadata', JSON.stringify(metadata))
    form.append('file', file, `${metadata.id}.jpg`)
    const res = await api.post<unknown>('/sync/fotos', form)
    return FotoSyncResponseSchema.parse(res.data)
  },

  /**
   * Recupera signed URL pra uma foto já sincronizada. Usado como fallback quando
   * o Blob local foi evictado pelo Safari (ícone "?" no <img src="blob:...">).
   * Backend devolve URL assinada de 15min do Supabase Storage.
   */
  async fotoSignedUrl(appUuid: string): Promise<FotoSignedUrlResponse> {
    const res = await api.get<unknown>(`/sync/fotos/${encodeURIComponent(appUuid)}/signed-url`)
    return FotoSignedUrlResponseSchema.parse(res.data)
  },
}

// ============================================================================
//  Parte Diária (MVE / EPP) — leituras dependem de data/chefe (sem ETag)
// ============================================================================
export const parteDiariaEndpoints = {
  /** MVEs do chefe no mês de `dt` (A.1 ∪ A.2) com estado (sem/com lançamento, gerada). */
  async mves(dt: string): Promise<MvesParteDiariaResponse> {
    const res = await api.get<unknown>(`/parte-diaria/mves?${new URLSearchParams({ dt })}`)
    return MvesParteDiariaResponseSchema.parse(res.data)
  },
  /** Tudo que o formulário precisa + bloqueios do dia (é o "precheck" do módulo). */
  async contexto(mveId: number, dt: string): Promise<ContextoParteDiariaDto> {
    const res = await api.get<unknown>(`/parte-diaria/contexto?${new URLSearchParams({ mve_id: String(mveId), dt })}`)
    return ContextoParteDiariaSchema.parse(res.data)
  },
  async obras(mveId?: number): Promise<ObraParteDto[]> {
    const res = await api.get<unknown>(`/parte-diaria/obras${mveId != null ? `?mve_id=${mveId}` : ''}`)
    return z.object({ items: z.array(ObraParteDtoSchema) }).parse(res.data).items
  },
  async servicos(mveId: number, obraId: number): Promise<ServicoParteDto[]> {
    const params = new URLSearchParams({ mve_id: String(mveId), obra_id: String(obraId) })
    const res = await api.get<unknown>(`/parte-diaria/servicos?${params}`)
    return z.object({ items: z.array(ServicoParteDtoSchema) }).parse(res.data).items
  },
  /** Pré-carga offline: MVEs + obras + equipes + serviços fatorados numa chamada só. */
  async pacoteOffline(dt: string): Promise<PacoteOfflineParteDiariaDto> {
    const res = await api.get<unknown>(`/parte-diaria/pacote-offline?${new URLSearchParams({ dt })}`, { timeoutMs: 60_000 })
    return PacoteOfflineParteDiariaSchema.parse(res.data)
  },
  async equipes(obraId: number): Promise<EquipeParteDto[]> {
    const res = await api.get<unknown>(`/parte-diaria/equipes?${new URLSearchParams({ obra_id: String(obraId) })}`)
    return z.object({ items: z.array(EquipeParteDtoSchema) }).parse(res.data).items
  },
  /** Grava o dia da MVE (principal + frações). 2xx = gravado; resposta fora do schema NÃO vira erro. */
  async syncMve(dto: LancamentoMveDto): Promise<ParteDiariaMveSyncResponse> {
    const res = await api.post<unknown>('/sync/parte-diaria/mve', dto)
    const parsed = ParteDiariaMveSyncResponseSchema.safeParse(res.data)
    if (!parsed.success) {
      console.warn('[sync/parte-diaria/mve] resposta fora do schema (aceita):', parsed.error.flatten())
      return res.data as ParteDiariaMveSyncResponse
    }
    return parsed.data
  },
  /** Grava o período do EPP e recalcula o mês. Mesma regra de safeParse. */
  async syncEpp(dto: LancamentoEppDto): Promise<ParteDiariaEppSyncResponse> {
    const res = await api.post<unknown>('/sync/parte-diaria/epp', dto)
    const parsed = ParteDiariaEppSyncResponseSchema.safeParse(res.data)
    if (!parsed.success) {
      console.warn('[sync/parte-diaria/epp] resposta fora do schema (aceita):', parsed.error.flatten())
      return res.data as ParteDiariaEppSyncResponse
    }
    return parsed.data
  },
}

// ============================================================================
//  Carreteiro de frota própria — fichas que o servidor está guardando
// ============================================================================
const FichaPropriaNoServidorSchema = z
  .object({
    id: z.string(),
    dtMovimentacao: z.string(),
    mveId: z.number(),
    obraId: z.number(),
    ficha: z.string().nullable(),
    viagens: z.number(),
    recebidaEm: z.string(),
    motivo: z.string().nullable(),
    resultado: z.string().optional(),
  })
  .passthrough()
export type FichaPropriaNoServidor = z.infer<typeof FichaPropriaNoServidorSchema>

export const carreteiroProprioEndpoints = {
  /** Fichas do usuário aguardando a parte diária + as que o servidor não conseguiu gravar (últimos 30 dias). */
  async pendentes(): Promise<{ pendentes: FichaPropriaNoServidor[]; recusadas: FichaPropriaNoServidor[] }> {
    const res = await api.get<unknown>('/carreteiro/proprio/pendentes', { timeoutMs: 30_000 })
    return z
      .object({ pendentes: z.array(FichaPropriaNoServidorSchema), recusadas: z.array(FichaPropriaNoServidorSchema) })
      .parse(res.data)
  },
}
